/**
 * Visitor location lifecycle. A chosen location lives ONLY in memory +
 * sessionStorage for at most 24h, is never written to localStorage, never
 * placed in a URL, and is cleared on demand. This prevents a durable or
 * shareable location profile.
 *
 * GPS is never the starting state. Until the visitor explicitly chooses a
 * place (manual text or the "Use my location" control), the query origin is
 * Seattle Center (`source: 'default'`). That default is not persisted — it is
 * not the visitor's location.
 */

export interface Coordinates {
  lat: number;
  lng: number;
}

/**
 * Seattle Center. Matches `config/geocode/seattle-places.json`
 * (`key: "seattle center"`), not the downtown metro centroid.
 */
export const SEATTLE_CENTER: Coordinates = { lat: 47.6249, lng: -122.3517 };

export type LocationSource = 'default' | 'gps' | 'manual';

export type LocationState =
  | { status: 'ready'; coordinates: Coordinates; source: LocationSource; label?: string }
  | { status: 'requesting'; coordinates: Coordinates; source: LocationSource; label?: string }
  | { status: 'resolving'; coordinates: Coordinates; source: LocationSource; label?: string }
  | {
      status: 'error';
      reason: 'denied' | 'unavailable' | 'unresolved';
      coordinates: Coordinates;
      source: LocationSource;
      label?: string;
    };

/** Settled browse from Seattle Center. Does not request GPS. */
export function defaultLocationState(): LocationState {
  return { status: 'ready', coordinates: { ...SEATTLE_CENTER }, source: 'default' };
}

export interface SessionLocation {
  coordinates: { lat: number; lng: number };
  source: 'gps' | 'manual';
  storedAt: number;
  /** The text they typed. Absent for GPS and for sessions saved before it. */
  label?: string;
}

export const SESSION_KEY = 'fef.location.v1';
export const MAX_AGE_MS = 24 * 60 * 60 * 1000;

/** Minimal storage interface (sessionStorage in the browser, a Map in tests). */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Load a non-expired session location, or null. Corrupt/expired => null (and cleared). */
export function loadSessionLocation(storage: KeyValueStorage, now: number): SessionLocation | null {
  const raw = storage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as SessionLocation;
    if (
      typeof parsed?.coordinates?.lat !== 'number' ||
      typeof parsed?.coordinates?.lng !== 'number' ||
      typeof parsed?.storedAt !== 'number'
    ) {
      storage.removeItem(SESSION_KEY);
      return null;
    }
    if (now - parsed.storedAt > MAX_AGE_MS) {
      storage.removeItem(SESSION_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(SESSION_KEY);
    return null;
  }
}

export function saveSessionLocation(
  storage: KeyValueStorage,
  coordinates: { lat: number; lng: number },
  source: 'gps' | 'manual',
  now: number,
  label?: string,
): void {
  const value: SessionLocation = {
    coordinates,
    source,
    storedAt: now,
    ...(label ? { label } : {}),
  };
  storage.setItem(SESSION_KEY, JSON.stringify(value));
}

/** True when two lookups are the same point. A snapped geocode did not move. */
export function sameCoordinates(a: Coordinates, b: Coordinates): boolean {
  return Math.abs(a.lat - b.lat) < 1e-5 && Math.abs(a.lng - b.lng) < 1e-5;
}

/** Visible origin pin. GPS says You. Otherwise the place name, or Seattle Center. */
export function originPinLabel(state: Pick<LocationState, 'source' | 'label'>): string {
  if (state.source === 'gps') return 'You';
  if (state.source === 'manual' && state.label) return state.label;
  return 'Seattle Center';
}

export function clearSessionLocation(storage: KeyValueStorage): void {
  storage.removeItem(SESSION_KEY);
}

/** In-memory storage for tests. */
export class MemoryStorage implements KeyValueStorage {
  private readonly m = new Map<string, string>();
  getItem(key: string): string | null {
    return this.m.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.m.set(key, value);
  }
  removeItem(key: string): void {
    this.m.delete(key);
  }
  keys(): string[] {
    return [...this.m.keys()];
  }
}

/**
 * Location resolution reducer. Given the current state and an action, return the
 * next state. Pure and deterministic so the lifecycle is testable without a DOM.
 */
export type LocationAction =
  | { type: 'request_gps' }
  | { type: 'gps_success'; lat: number; lng: number }
  | { type: 'gps_denied' }
  | { type: 'gps_unavailable' }
  | { type: 'manual_submit' }
  | { type: 'manual_resolved'; lat: number; lng: number; label: string }
  | { type: 'manual_unresolved' }
  | { type: 'clear' };

function carry(state: LocationState): {
  coordinates: Coordinates;
  source: LocationSource;
  label?: string;
} {
  return {
    coordinates: state.coordinates,
    source: state.source,
    ...(state.label ? { label: state.label } : {}),
  };
}

export function locationReducer(state: LocationState, action: LocationAction): LocationState {
  switch (action.type) {
    case 'request_gps':
      // Explicit only. The previous origin stays in place until GPS actually returns,
      // so the list does not disappear behind a permission prompt.
      return { status: 'requesting', ...carry(state) };
    case 'gps_success':
      return { status: 'ready', coordinates: { lat: action.lat, lng: action.lng }, source: 'gps' };
    case 'gps_denied':
      return { status: 'error', reason: 'denied', ...carry(state) };
    case 'gps_unavailable':
      return { status: 'error', reason: 'unavailable', ...carry(state) };
    case 'manual_submit':
      return { status: 'resolving', ...carry(state) };
    case 'manual_resolved':
      return {
        status: 'ready',
        coordinates: { lat: action.lat, lng: action.lng },
        source: 'manual',
        label: action.label,
      };
    case 'manual_unresolved':
      return { status: 'error', reason: 'unresolved', ...carry(state) };
    case 'clear':
      return defaultLocationState();
    default:
      return state;
  }
}
